import { useEffect } from 'react';

type ModifierOpts = {
  meta?: boolean; // ⌘ on macOS, Win key on Windows
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
};

const isTextInput = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
};

export const useShortcut = (
  keys: string | string[],
  modifiers: ModifierOpts,
  handler: (e: KeyboardEvent) => void,
  opts: { allowInInput?: boolean } = {},
) => {
  useEffect(() => {
    const targets = (Array.isArray(keys) ? keys : [keys]).map((k) => k.toLowerCase());
    const onKey = (e: KeyboardEvent) => {
      if (!opts.allowInInput && isTextInput(e.target)) return;
      const wantMeta = modifiers.meta ?? false;
      const wantCtrl = modifiers.ctrl ?? false;
      const wantShift = modifiers.shift ?? false;
      const wantAlt = modifiers.alt ?? false;

      // Either meta or ctrl satisfies a "command" intent across platforms when both flags are set.
      const cmdLike = wantMeta || wantCtrl;
      const cmdOk = cmdLike ? e.metaKey || e.ctrlKey : !e.metaKey && !e.ctrlKey;

      if (!cmdOk) return;
      if (e.shiftKey !== wantShift) return;
      if (e.altKey !== wantAlt) return;
      if (!targets.includes(e.key.toLowerCase())) return;

      e.preventDefault();
      handler(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keys, modifiers, handler, opts.allowInInput]);
};
